import {getActiveEmployeeByEmail} from '@/shared/api/sharedEmployeesApi.ts';
import {supabase} from '@/shared/api/supabase.ts';
import {queryKeys} from '@/shared/hooks/queryKeys.ts';
import {UserRole} from '@/shared/types/common.ts';
import {Employee} from '@/shared/types/employee.ts';
import {
    clearStoredSession,
    getStoredSession,
    hasOAuthErrorCallback,
    hasOAuthSuccessCallback,
} from '@/shared/utils/auth.ts';
import {Session, User} from '@supabase/supabase-js';
import {useQueryClient} from '@tanstack/react-query';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

// ============================================================================
// Types
// ============================================================================

type AuthContextType = {
    user: User | null;
    employee: Employee | null;
    session: Session | null;
    role: UserRole | null;
    loading: boolean;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
    isAuthenticated: boolean;
    hasAccess: boolean;
};

type AuthState = {
    user: User | null;
    employee: Employee | null;
    session: Session | null;
    loading: boolean;
};

// ============================================================================
// Constants
// ============================================================================

/**
 * Timeout for OAuth callback processing (10 seconds).
 * Prevents infinite loading if OAuth callback fails silently.
 */
const OAUTH_CALLBACK_TIMEOUT_MS = 10_000;

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({children}: {children: ReactNode}) => {
    const [state, setState] = useState<AuthState>({
        user: null,
        employee: null,
        session: null,
        loading: true,
    });

    const queryClient = useQueryClient();
    const initializedRef = useRef(false);

    /**
     * Fetch employee and populate React Query cache.
     */
    const fetchEmployee = useCallback(
        async (email: string): Promise<Employee | null> => {
            try {
                const employee = await getActiveEmployeeByEmail(email);
                queryClient.setQueryData(queryKeys.employees.byEmail(email), employee);
                return employee;
            } catch (error) {
                console.error('Error fetching employee:', error);
                return null;
            }
        },
        [queryClient]
    );

    /**
     * Process a session and update state.
     */
    const processSession = useCallback(
        async (session: Session | null) => {
            if (!session?.user?.email) {
                setState({user: null, employee: null, session: null, loading: false});
                return;
            }

            // Set user/session immediately
            setState((prev) => ({
                ...prev,
                user: session.user,
                session,
            }));

            const employee = await fetchEmployee(session.user.email);

            setState({
                user: session.user,
                employee,
                session,
                loading: false,
            });
        },
        [fetchEmployee]
    );

    // Initialize auth state
    useEffect(() => {
        let mounted = true;
        let oauthTimeoutId: ReturnType<typeof setTimeout> | null = null;

        // Handle OAuth error callback - clear hash and redirect to unauthenticated state
        if (hasOAuthErrorCallback()) {
            console.error('OAuth callback error detected in URL hash');
            window.history.replaceState({}, '', window.location.pathname);
            initializedRef.current = true;
            setState({user: null, employee: null, session: null, loading: false});
            return;
        }

        // For successful OAuth callback, wait for Supabase to process the hash
        if (hasOAuthSuccessCallback()) {
            // Set a timeout to prevent infinite loading if callback processing fails
            oauthTimeoutId = setTimeout(() => {
                if (mounted && !initializedRef.current) {
                    console.error('OAuth callback timeout - falling back to localStorage');
                    window.history.replaceState({}, '', window.location.pathname);

                    // Try to initialize from localStorage as fallback
                    const storedSession = getStoredSession();
                    if (storedSession) {
                        processSession(storedSession);
                    } else {
                        setState({user: null, employee: null, session: null, loading: false});
                    }
                }
            }, OAUTH_CALLBACK_TIMEOUT_MS);

            const {
                data: {subscription},
            } = supabase.auth.onAuthStateChange(async (event, session) => {
                if (!mounted) {
                    return;
                }

                // Handle both SIGNED_IN and INITIAL_SESSION events
                if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
                    if (oauthTimeoutId) {
                        clearTimeout(oauthTimeoutId);
                        oauthTimeoutId = null;
                    }
                    initializedRef.current = true;
                    await processSession(session);
                    window.history.replaceState({}, '', window.location.pathname);
                    subscription.unsubscribe();
                }
            });

            return () => {
                mounted = false;
                if (oauthTimeoutId) {
                    clearTimeout(oauthTimeoutId);
                }
                subscription.unsubscribe();
            };
        }

        // Normal page load - read from localStorage directly
        const initializeFromStorage = async () => {
            const storedSession = getStoredSession();

            if (storedSession && mounted) {
                initializedRef.current = true;
                await processSession(storedSession);
            } else if (mounted) {
                initializedRef.current = true;
                setState({user: null, employee: null, session: null, loading: false});
            }
        };

        initializeFromStorage();

        // Listen for sign out and token refresh events
        const {
            data: {subscription},
        } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (!mounted) {
                return;
            }

            if (event === 'SIGNED_OUT') {
                setState({user: null, employee: null, session: null, loading: false});
            }

            if (event === 'TOKEN_REFRESHED' && session) {
                setState((prev) => ({
                    ...prev,
                    session,
                    user: session.user,
                }));
            }
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, [processSession]);

    // Sign in with Google
    const signInWithGoogle = useCallback(async () => {
        const {error} = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: window.location.origin,
            },
        });

        if (error) {
            console.error('Google sign in error:', error);
            throw error;
        }
    }, []);

    // Sign out
    const signOut = useCallback(async () => {
        if (state.user?.email) {
            queryClient.removeQueries({
                queryKey: queryKeys.employees.byEmail(state.user.email),
            });
        }

        // Clear localStorage and state immediately
        clearStoredSession();
        setState({user: null, employee: null, session: null, loading: false});

        // Also call Supabase signOut (non-blocking)
        supabase.auth.signOut().catch((error) => {
            console.error('Sign out error:', error);
        });
    }, [queryClient, state.user?.email]);

    const value = useMemo<AuthContextType>(
        () => ({
            user: state.user,
            employee: state.employee,
            session: state.session,
            role: state.employee?.role ?? null,
            loading: state.loading,
            signInWithGoogle,
            signOut,
            isAuthenticated: !!state.user,
            hasAccess: !!state.employee,
        }),
        [state, signInWithGoogle, signOut]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
